<script setup lang="ts">
import { ref, onMounted, onUnmounted, defineAsyncComponent } from 'vue'
import Dashboard from './views/Dashboard.vue'
import UpdateAvailablePill from './components/UpdateAvailablePill.vue'
import { useBuildVersion } from './composables/useBuildVersion'
import { isChartId, type ChartId } from './components/chartIds'

// 2026-11-03 (Paolo: the phone kept the old build in memory after a deploy): watch the served bundle hash; reload when the
// app comes back to the foreground on a new build, else offer a tap-to-reload pill.
const build = useBuildVersion()

// Chart bundle is lightweight-charts-heavy (~200KB). Lazy-load so the
// dashboard's initial render doesn't pull it in — only fetched on the
// first /charts/* navigation. Suspense isn't strictly required since the
// async component swap is fast on a warm CDN, but a loading fallback
// avoids a blank flash on slow connections.
const ChartView = defineAsyncComponent({
  loader: () => import('./views/ChartView.vue'),
  loadingComponent: { template: '<div style="color:#888;padding:20px;text-align:center;font-family:JetBrains Mono,monospace">Loading chart…</div>' },
  delay: 200,
})

type RouteState = { kind: 'dashboard' } | { kind: 'chart'; id: ChartId }

function resolveRoute(): RouteState {
  // Check hash-based route first (#/charts/gold-gvz)
  const hash = window.location.hash
  let m = hash.match(/#\/charts\/([^/]+)\/?$/)
  if (m && isChartId(m[1])) {
    return { kind: 'chart', id: m[1] }
  }

  // Fallback to pathname-based route (/charts/gold-gvz) for non-SPA setups
  const path = window.location.pathname
  m = path.match(/^\/charts\/([^/]+)\/?$/)
  if (m && isChartId(m[1])) {
    return { kind: 'chart', id: m[1] }
  }

  return { kind: 'dashboard' }
}

const route = ref<RouteState>(resolveRoute())

function onPopState() { route.value = resolveRoute() }

onMounted(() => window.addEventListener('popstate', onPopState))
onUnmounted(() => window.removeEventListener('popstate', onPopState))
</script>

<template>
  <div id="app">
    <UpdateAvailablePill :visible="build.updateAvailable.value" @reload="build.reload()" />
    <ChartView v-if="route.kind === 'chart'" :chart-id="route.id" />
    <Dashboard v-else />
  </div>
</template>

<style scoped>
#app {
  max-width: 960px;
  margin: 0 auto;
  padding: 0.5rem 1rem;
  font-family: system-ui, -apple-system, sans-serif;
}
</style>
